import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { internshipApi } from '@/api';
import {
  BoxList,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  ListCard,
  LoadingState,
  Pagination,
} from '@/components/ui';
import { InternshipCard } from '@/features/internships/InternshipCard';
import { useSaveInternship } from '@/features/internships/useSaveInternship';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';

const PAGE_SIZE = 10;

/** The "Saved" tab of My internships. Applying takes a role off this list. */
export function SavedInternshipsPanel() {
  const [page, setPage] = useState(1);
  const save = useSaveInternship();

  const { data, isPending, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: ['saved-internships', page],
    queryFn: () => internshipApi.listSaved({ page, page_size: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div className="flex flex-1 flex-col">
      <ListCard
        fill
        className="flex-1"
        isRefreshing={isPlaceholderData}
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Loading saved internships…" />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Icon name="bookmark" size={22} />}
              title="Nothing saved yet"
              description="Save internships while browsing to compare them later. Once you apply, a role moves to Applied."
              action={<LinkButton to={paths.internships}>Browse internships</LinkButton>}
            />
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <BoxList>
          {rows.map((saved) => (
            <li key={saved.id}>
              <InternshipCard
                internship={saved.internship}
                onToggleSave={save.canSave ? save.toggle : undefined}
                isSaving={save.isPending && save.pendingId === saved.internship.id}
              >
                {saved.note && (
                  <p className="text-ink-muted mt-2.5 flex items-start gap-1.5 text-xs italic">
                    <Icon name="pencil" size={12} className="mt-0.5 shrink-0" />
                    Note: {saved.note}
                  </p>
                )}
              </InternshipCard>
            </li>
          ))}
        </BoxList>
      </ListCard>
    </div>
  );
}
